import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Typography,
  Box,
  InputAdornment,
} from '@mui/material';

interface BudgetControlProps {
  value: number;
  onChange: (value: number) => void;
  minBudget?: number;
  maxBudget?: number;
}

const BudgetControl: React.FC<BudgetControlProps> = ({
  value,
  onChange,
  minBudget = 6,
  maxBudget = 1000000,
}) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState<string>(value.toString());
  const [error, setError] = useState<string>('');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Update local value when prop value changes (e.g., on dialog open)
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const validateAndUpdate = (inputValue: string) => {
    // Allow empty input for user convenience
    if (inputValue === '') {
      setError('');
      return;
    }

    const numValue = parseFloat(inputValue);

    // Validate numeric input
    if (isNaN(numValue)) {
      setError(t('settings.strategy.errors.invalidNumber'));
      return;
    }

    // Validate minimum
    if (numValue < minBudget) {
      setError(t('settings.strategy.errors.belowMin', { min: minBudget.toFixed(2) }));
      return;
    }

    // Validate maximum
    if (numValue > maxBudget) {
      setError(t('settings.strategy.errors.aboveMax', { max: maxBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
      return;
    }

    // Valid input
    setError('');

    // Clear existing debounce timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new debounce timer (300ms)
    const timer = setTimeout(() => {
      onChange(numValue);
    }, 300);

    setDebounceTimer(timer);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setLocalValue(inputValue);
    validateAndUpdate(inputValue);
  };

  const handleBlur = () => {
    // On blur, if empty, reset to current value
    if (localValue === '') {
      setLocalValue(value.toString());
      setError('');
    } else {
      // Ensure value is formatted
      const numValue = parseFloat(localValue);
      if (!isNaN(numValue)) {
        setLocalValue(numValue.toString());
      }
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Format value for display
  const displayValue = localValue;
  const formattedValue = parseFloat(localValue);
  const formattedDisplay = !isNaN(formattedValue)
    ? `$${formattedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';

  return (
    <Box>
      <TextField
        label={t('settings.strategy.budget')}
        type="number"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!error}
        helperText={
          error ||
          t('settings.strategy.helperText', {
            min: minBudget.toFixed(2),
            max: maxBudget.toLocaleString('en-US')
          })
        }
        fullWidth
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        inputProps={{
          min: minBudget,
          max: maxBudget,
          step: 0.01,
        }}
      />
      {!error && formattedDisplay && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t('settings.strategy.preview')}: {formattedDisplay}
        </Typography>
      )}
    </Box>
  );
};

export default BudgetControl;
